<template>
    <div :class="[inputCssClass, errorMessages ? 'field-error' : '']">
        <div class="global-label">{{ fieldLabel }}</div>
        <div>
            <div v-if="disabled" tabindex="0" class="disp">
                <div v-if="projectId && project" class="d-flex">
                    <div class="val ml-1 pr-1">
                        <router-link
                            :to="{ name: 'project_view', params: { id: project.id }}"
                            style="text-decoration: none"
                            @click.native="$emit('close_click')"
                        >
                            <icon :color="project.color" icon="ClipboardList" />
                        </router-link>
                        {{ formatProjectDisp(project) }}
                    </div>
                </div>
                <div v-else></div>
            </div>
            <v-menu
                v-else
                v-model="menuOpen"
                :transition="false"
                offset-y
                right
                :close-on-content-click="false"
            >
                <template v-slot:activator="{ on }">
                    <div tabindex="0" class="disp cursor-pointer" v-on="on">
                        <div v-if="projectId && project" class="d-flex">
                            <div class="val ml-1 pr-1">
                                <router-link
                                    :to="{ name: 'project_view', params: { id: project.id }}"
                                    style="text-decoration: none"
                                    @click.native="$emit('close_click')"
                                >
                                    <icon :color="project.color" icon="ClipboardList" />
                                </router-link>
                                {{ formatProjectDisp(project) }}
                            </div>
                        </div>
                        <div v-else>
                            <div class="val ml-2" style="color: var(--v-n4-base);">
                                {{ fieldEmptyText }}
                            </div>
                            <div :class="['arrow', menuOpen ? 'arrow--open' : '']"><v-icon>mdi-menu-down</v-icon></div>
                        </div>
                    </div>
                </template>
                <v-card>
                    <v-text-field
                        ref="filterBox"
                        v-model="keyword"
                        :placeholder="fieldKeywordText"
                        hide-details
                        class="menu-filter-text mt-0 pt-0 body-2"
                        @keypress.enter.esc="$emit('tab_next')"
                    ></v-text-field>
                    <v-list dense style="max-height: 500px; max-width: 273px; overflow: auto;">
                        <v-list-item
                            v-for="item in filtered_list"
                            :key="item.id"
                            style="min-height: 32px;"
                            class="pl-2"
                            @click="toggle_selection($event, item.id)"
                        >
                            <v-list-item-avatar :size="24" class="mr-2">
                                <icon :color="item.color" icon="ClipboardList" />
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title class="font-weight-regular subtitle-2">
                                    {{ item.name }}
                                </v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-icon v-if="projectId === item.id" color="primary">check</v-icon>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-menu>

            <div :class="['details', errorMessages ? 'error--text' : '']">
                {{ errorMessages }}
            </div>
        </div>
    </div>
</template>

<script>
import { $t } from "@/i18n";
import { ref, computed, watch } from "@vue/composition-api";
import AppModule from "../../store/modules/app";
import ProjectModule from "../../store/modules/project";
import useProjectInfo from "../../composables/use-project-info";
import { formatProjectStatus } from "../../composables/use-custom-fields";
import errorDialog from "@/views/ErrorDialog/errorDialog"

export default {
    name: "ProjectField",
    components: {
    },
    props: {
        label: {
            type: String,
            default: null
        },
        accountId: {
            type: Number,
            default: 0
        },
        clientId: {
            type: Number,
            default: 0
        },
        value: {
            type: Number,
            default: null
        },
        valueDisp: {
            type: [Object],
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        errorMessages: {
            type: String,
            default: null
        },
        inputCssClass: {
            type: String,
            default: "task-field"
        },
        emptyText: {
            type: String,
            default: null
        },
        showStatus: {
            type: Boolean,
            default: false
        },
        task: {
            type: Object,
            default: null
        }
    },
    setup(props, ctx) {
        const { custom_fields } = AppModule();
        const { setAccountId } = useProjectInfo(props.accountId);
        const { fetchProjects, project_status, isEventTypeCompany } = ProjectModule();

        const menuOpen = ref(false);
        const projectId = ref(props.value != null ? props.value : 0);
        const items = ref([]);
        const keyword = ref(null);
        const filterBox = ref(null);
        const accountId = computed(() => {
            return props.accountId;
        });
        const clientId = computed(() => {
            return props.clientId;
        });
        const fieldLabel = computed(() => {
            if (props.label != null) return props.label;
            const projectName = isEventTypeCompany(props.accountId) ? $t('Event') : $t('Project')

            return props.showStatus ? "{0} / {1}".format(projectName, $t("Status")) : projectName;
        });
        const fieldEmptyText = computed(() => {
            if (props.emptyText != null) return props.emptyText;
            return isEventTypeCompany(props.accountId) ? $t('Select event') : $t('Select project')
        });
        const fieldKeywordText = computed(() => {
            return isEventTypeCompany(props.accountId) ? $t('Find event...') : $t('Find project...')
        });
        const project = computed(function() {
            if (projectId.value === 0) return null;
            let ret = items.value.find(x => x.id === projectId.value);
            if (ret != null) return ret;
            if (props.valueDisp != null && props.valueDisp.id === projectId.value) return props.valueDisp;
            return { name: "#{0}".format(projectId.value) };
        });
        const filtered_list = computed(function() {
            let size = 50;
            let ret = [];
            if (!keyword.value) {
                ret = items.value.slice(0, size);
                return ret;
            }
            let key = keyword.value.toLowerCase();
            return items.value.filter(x => x.name.toLowerCase().indexOf(key) >= 0).slice(0, size);
        });
        const watchValue = computed(() => {
            return props.value;
        });

        const loadProjects = () => {
            if (!clientId.value) {
                items.value = [];
                return;
            }
            let params = {
                account_id: accountId.value,
                client_id: clientId.value,
                itemsPerPage: 9999,
                project_status: [project_status.value.active, project_status.value.cancelled],
                current_project_id: projectId.value
            };
            fetchProjects(params)
                .then(resp => {
                    items.value = resp.items;

                    if (props.task && props.task.project_id && !props.task.project && project.value) {
                        props.task.project = project.value
                    }
                })
                .catch(ex => errorDialog.showError(ex));
        };
        const toggle_selection = (e, id) => {
            if (projectId.value === id) {
                projectId.value = 0;
            } else {
                projectId.value = id;
                menuOpen.value = false;
                keyword.value = null;
            }
        };
        const formatProjectDisp = proj => {
            if (!props.showStatus)
                return proj.name;
            let fld = custom_fields.value[accountId.value].find(x => x.type === "project_status");
            if (!proj.status || !fld)
                return proj.name;
            return "{0} / {1}".format(proj.name, formatProjectStatus(
                proj, { choices: fld._data ? fld._data.choices : []})
            );
        }

        watch(accountId, (val) => {
            setAccountId(val);
        });
        watch(clientId, () => {
            loadProjects();
        });
        watch(menuOpen, (val, prev_val) => {
            if (prev_val === undefined) return;

            if (val) {
                setTimeout(() => {
                    filterBox.value.focus();
                }, 200);
            }
            else if (props.value !== projectId.value) {
                if (projectId.value === 0) {
                    ctx.emit("change", null);
                } else {
                    const project = items.value.find(x => x.id === projectId.value)
                    ctx.emit("change", project);
                }
            }
        });
        watch(watchValue, (val) => {
            projectId.value = val != null ? val : 0;
        });

        return {
            menuOpen,
            projectId,
            project,
            fieldLabel,
            fieldEmptyText,
            fieldKeywordText,
            keyword,
            filtered_list,
            watchValue,
            filterBox,
            toggle_selection,
            formatProjectDisp
        };
    }
};
</script>
